import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import TweetComponent from "./TweetPage";
import { Container } from "@/components/Container";
import ImageGenerationPage from "./ImageGenerationPage";
import ThreadsComponent from "@/components/ThreadsComponent";
import LongFormContentComponent from "@/components/LongForm";
import { usePersonasQueryNoPublic } from "@/api/personas";
import { useSelectedPersona } from "@/hooks/use-selected-persona";
import { Label } from "@/components/ui/label";
import {
  Check,
  ChevronsUpDown,
  Hash,
  Instagram,
  FileText,
  Loader2,
} from "lucide-react";
import { X } from "@/components/logos/X";
import { Button } from "@/components/ui/button";
import { Role } from "@/components/AuthorizationProvider";
import CreditWarningModal from "@/components/CreditWarningModal";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/use-auth";

const Tab = {
  Tweets: "tweets",
  Threads: "threads",
  Images: "images",
  LongForm: "long-form-alpha",
  // New tab for manual approvals
  HumanApprovals: "human-approvals",
} as const;

type Tab = (typeof Tab)[keyof typeof Tab];

const TABS = Object.values(Tab);

const EmptyStateModal = ({ tab }: { tab: string | undefined }) => {
  const navigate = useNavigate();

  const modalContent = {
    tweets: {
      title: "Create an Agent to generate Tweets",
      description:
        "Create tweets based on your agents worldview and current events. To start generating tweets, you'll need to create an agent first",
      imageSrc: "/guidance-images/tweet-creation.png",
      nextRoute: "/agents/new",
      closeRoute: "/create/threads",
    },
    threads: {
      title: "Create an Agent to build X Threads",
      description:
        "Create threads based on custom topics or youtube videos. To start generating thread content, you'll need to create an agent first",
      imageSrc: "/guidance-images/thread-creation.png",
      nextRoute: "/agents/new",
      closeRoute: "/create/images",
    },
    images: {
      title: "Create an Agent to produce AI Images",
      description:
        "Generate AI images of your AI agent using SOTA text to image models. To start generating AI images, you'll need to create an agent first",
      imageSrc: "/guidance-images/image-creation.png",
      nextRoute: "/agents/new",
      closeRoute: "/create/human-approvals",
    },
    "human-approvals": {
      title: "Create an Agent for Human Approval Flow",
      description:
        "Manage your agent's replies with a manual approval process before they go live on Twitter.",
      imageSrc: "/guidance-images/manual-approval.png",
      nextRoute: "/agents/new",
      closeRoute: "/agents/",
    },
  };

  const content =
    modalContent[tab as keyof typeof modalContent] || modalContent["tweets"]; // Default to tweets if tab not found

  const handleClose = () => {
    navigate(content.closeRoute, { replace: true });
  };

  return (
    <Dialog open={true} onOpenChange={handleClose}>
      <DialogContent className="bg-black/100 border-white/10 border-border md:max-w-[50%] w-[40vw] h-[75vh]">
        <DialogHeader>
          <DialogTitle className="text-primary text-xl">
            {content.title}
          </DialogTitle>
          <DialogDescription className="text-white/60 text-lg">
            {content.description}
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 flex items-center justify-center h[20%] border-border">
          <img
            src={content.imageSrc}
            alt="Guidance"
            className="h-[45vh] object-contain"
          />
        </div>
        <div className="flex justify-center">
          <Button
            className="bg-[#DFFE00] hover:bg-[#DFFE00]/90 text-black font-medium px-8 py-3"
            onClick={() => navigate(content.nextRoute, { replace: true })}
          >
            Create Agent
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

function Create() {
  const {
    data: personas = [],
    isError,
    isLoading: personasLoading,
  } = usePersonasQueryNoPublic();
  const [selectedPersonaId, setSelectedPersonaId] = useSelectedPersona();
  const { search } = useLocation();
  const { tab } = useParams();
  const [error, setError] = useState<string | null>(null);
  const [personaOpen, setPersonaOpen] = useState(false);
  const { hasAtLeastRole } = useAuth();
  const navigate = useNavigate();

  const isAdmin = hasAtLeastRole(Role.TenantAdmin);

  const selectedPersona = personas.find(
    (persona) => persona.persona_id === selectedPersonaId,
  );

  useEffect(() => {
    if (!tab || !TABS.includes(tab as Tab)) {
      navigate("/create/tweets", { replace: true });
    }
  }, [tab, navigate]);

  // Ensure we have a selected persona
  useEffect(() => {
    if (!personasLoading && personas.length > 0) {
      if (
        !selectedPersonaId ||
        !personas.some((p) => p.persona_id === selectedPersonaId)
      ) {
        setSelectedPersonaId(personas[0].persona_id, { replace: true });
      }
    }
  }, [personas, selectedPersonaId, setSelectedPersonaId, personasLoading]);

  useEffect(() => {
    if (isError) setError("Error loading agents. Please try again later.");
  }, [isError]);

  // Show loading spinner while personas are loading
  if (personasLoading) {
    return (
      <Container>
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </div>
      </Container>
    );
  }

  const renderContent = () => {
    // If the user has no personas, show the empty state modal
    if (personas.length === 0) {
      return <EmptyStateModal tab={tab} />;
    }

    // If we have personas but none selected, try to select the first one
    if (!selectedPersona && personas.length > 0) {
      setSelectedPersonaId(personas[0].persona_id, { replace: true });
      return null;
    }

    // Don't render content if we don't have a selected persona
    if (!selectedPersona) {
      return null;
    }

    switch (tab) {
      case Tab.Tweets:
        return (
          <TweetComponent
            key={selectedPersonaId}
            personaId={selectedPersonaId}
            personaData={selectedPersona}
          />
        );
      case Tab.Threads:
        return (
          <ThreadsComponent
            key={selectedPersonaId}
            personaId={selectedPersonaId}
            personaData={selectedPersona}
          />
        );
      case Tab.Images:
        return (
          <div className="flex flex-row justify-center items-center w-full mt-2">
            <ImageGenerationPage />
          </div>
        );
      case Tab.LongForm:
        return (
          <LongFormContentComponent
            key={selectedPersonaId}
            personaId={selectedPersonaId}
            personaData={selectedPersona}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Container>
      {error && <p className="text-red-500 text-center mt-4">{error}</p>}
      {selectedPersonaId && (
        <CreditWarningModal
          agentId={selectedPersonaId}
          minimumCredits={10}
          showDebugInfo={import.meta.env.DEV}
        />
      )}
      <div className="flex flex-col justify-center items-center py-3">
        <div className="flex flex-row justify-center items-center gap-3 w-full max-w-xl px-4">
          <Label htmlFor="persona-selector">Agent</Label>
          <Popover open={personaOpen} onOpenChange={setPersonaOpen}>
            <PopoverTrigger asChild>
              <Button
                id="persona-selector"
                data-testid="persona-selector"
                variant="outline"
                role="combobox"
                aria-expanded={personaOpen}
                className="w-full max-w-64"
              >
                <span className="w-full truncate">
                  {selectedPersonaId && selectedPersona
                    ? selectedPersona.name
                    : "Select agent..."}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0">
              <Command>
                <CommandInput placeholder="Search agent..." />
                <CommandList>
                  <CommandEmpty>No agent found.</CommandEmpty>
                  <CommandGroup>
                    {personas
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((persona) => (
                        <CommandItem
                          key={persona.persona_id}
                          value={persona.persona_id}
                          onSelect={(value) => {
                            setPersonaOpen(false);
                            setSelectedPersonaId(value);
                          }}
                          keywords={[persona.name]}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              selectedPersonaId === persona.persona_id
                                ? "opacity-100"
                                : "opacity-0",
                            )}
                          />
                          {persona.name}
                        </CommandItem>
                      ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {/* Top Navigation for the Tab */}
        <div className="flex flex-row flex-wrap justify-center gap-2 mt-8 px-4">
          <Link to={{ pathname: `/create/${Tab.Tweets}`, search }}>
            <Button
              variant={tab === Tab.Tweets ? "default" : "outline"}
              className="w-[140px] flex items-center justify-center gap-2"
            >
              <X width={16} height={16} fill="currentColor" />
              Tweets
            </Button>
          </Link>

          <Link to={{ pathname: `/create/${Tab.Threads}`, search }}>
            <Button
              variant={tab === Tab.Threads ? "default" : "outline"}
              className="w-[140px] flex items-center justify-center gap-2"
            >
              <Hash className="h-4 w-4" />
              Threads
            </Button>
          </Link>

          <Link to={{ pathname: `/create/${Tab.Images}`, search }}>
            <Button
              variant={tab === Tab.Images ? "default" : "outline"}
              className="w-[140px] flex items-center justify-center gap-2"
            >
              <Instagram className="h-4 w-4" />
              Images
            </Button>
          </Link>

          {isAdmin && (
            <Link to={{ pathname: `/create/${Tab.LongForm}`, search }}>
              <Button
                variant={tab === Tab.LongForm ? "default" : "outline"}
                className="w-[160px] flex items-center justify-center gap-2"
              >
                <FileText className="h-4 w-4" />
                Long Form
              </Button>
            </Link>
          )}
        </div>
      </div>
      {renderContent()}
    </Container>
  );
}

export default Create;
